import { useState, useCallback } from "react";

interface PinEditState {
  active: boolean;
  locationId: string | null;
  originalPosition: google.maps.LatLngLiteral | null;
  draggedPosition: google.maps.LatLngLiteral | null;
  hasMoved: boolean;
}

const INITIAL_STATE: PinEditState = {
  active: false,
  locationId: null,
  originalPosition: null,
  draggedPosition: null,
  hasMoved: false,
};

export function usePinEditMode() {
  const [state, setState] = useState<PinEditState>(INITIAL_STATE);

  const enter = useCallback((locationId: string, position: google.maps.LatLngLiteral) => {
    setState({
      active: true,
      locationId,
      originalPosition: position,
      draggedPosition: position,
      hasMoved: false,
    });
  }, []);

  const onDrag = useCallback((lat: number, lng: number) => {
    setState((prev) => ({
      ...prev,
      draggedPosition: { lat, lng },
      hasMoved: true,
    }));
  }, []);

  const save = useCallback((): { lat: number; lng: number } | null => {
    if (!state.draggedPosition || !state.hasMoved) return null;
    // Don't reset state here — keep draggedPosition visible until the caller
    // confirms the mutation succeeded, then call reset().
    return { lat: state.draggedPosition.lat, lng: state.draggedPosition.lng };
  }, [state.draggedPosition, state.hasMoved]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const cancel = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    active: state.active,
    locationId: state.locationId,
    originalPosition: state.originalPosition,
    draggedPosition: state.draggedPosition,
    hasMoved: state.hasMoved,
    enter,
    onDrag,
    save,
    reset,
    cancel,
  };
}
